import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCardData } from 'team/hooks/useCardData';
import TeamMemberEditLayout from 'team/components/editMember/TeamMemberEditLayout';
import TeamMemberEditContent from 'team/components/editMember/TeamMemberEditContent';
import TeamMemberEditPreview from 'team/components/editMember/TeamMemberEditPreview';
import TeamChooseTemplateModal from 'team/components/editMember/TeamChooseTemplateModal';
import { applyTemplateToMember, assignMembersToTemplate } from 'team/apis/teamTemplateApi';
import { useTeam } from 'team/contexts/teamContext';
import { useCardContext } from 'contexts/CardContext';
import { getCardById } from 'apis/cardApi';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';

const TeamMemberEditDashboard = ({ user }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { cardId } = useParams();
    const { currentTeamId } = useTeam();
    const {
        loadCardFromServer,
        updateCardData,
        setCardCoverPhotoUrl,
        setCardProfilePictureUrl,
        setCardCompanyLogoUrl,
        setCardTheme,
        setCardLinkColor,
        setContextCardLayout,
        setCardBio,
        setCustomLinkTypeLabels
    } = useCardContext();
    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Load card data
    useCardData();

    // Extract the section from the query parameter
    const queryParams = new URLSearchParams(location.search);
    const initialSection = queryParams.get('section') || 'about';

    // Check if this is a new card creation flow
    const isNewCard = location.pathname.includes('add-card');
    // Check if coming from accessory activation
    const isFromAccessory = location.search.includes('section=accessories');

    const [selectedItem, setSelectedItem] = useState(() => {
        if (isNewCard) return 'about';
        if (isFromAccessory) return 'accessories';
        return 'about';
    });

    // Handle URL management
    useEffect(() => {
        // Only manage URL with search params for accessory activation
        if (isFromAccessory) {
            const queryParams = new URLSearchParams(location.search);
            const section = queryParams.get('section');
            setSelectedItem(section || 'accessories');
        }
    }, [location.search, isFromAccessory]);

    const handleMenuItemClick = (item) => {
        setSelectedItem(item);
        // Keep URLs clean - only use search params for accessories
        if (isFromAccessory) {
            navigate(`/team/members/card/${cardId}?section=${item}`);
        } else if (isNewCard) {
            navigate(`/team/members/card/${cardId}`);
        } else {
            navigate(`/team/members/card/${cardId}`);
        }
    };

    const handleOpenTemplateModal = () => {
        setTemplateModalOpen(true);
    };

    const handleCloseTemplateModal = () => {
        setTemplateModalOpen(false);
    };

    const handleSelectTemplate = async (template) => {
        if (!currentTeamId || !template || !cardId) {
            setSnackbar({
                open: true,
                message: 'Missing required information to apply template',
                severity: 'error'
            });
            return;
        }

        try {
            // Get card to find userId (memberId)
            const cardResponse = await getCardById(cardId);
            if (!cardResponse || !cardResponse.card || !cardResponse.card.userId) {
                throw new Error('Could not find card owner');
            }

            const memberId = cardResponse.card.userId;

            // Apply template to member's card
            const response = await applyTemplateToMember(currentTeamId, template._id, {
                memberId,
                cardId
            });

            setSnackbar({
                open: true,
                message: `Template "${template.templateName}" applied successfully`,
                severity: 'success'
            });

            // Refresh card data from server to show updated card
            try {
                // First, load fresh data from server using loadCardFromServer
                await loadCardFromServer(cardId);

                // Get fresh card data after template application
                const cardResponse = await getCardById(cardId);
                if (cardResponse && cardResponse.card) {
                    const updatedCard = cardResponse.card;

                    // Update card context with fresh data from server using updateCardData
                    await updateCardData(cardId, {
                        cardTheme: updatedCard.cardTheme || '',
                        cardLinkColor: updatedCard.linkColor || '',
                        contextCardLayout: updatedCard.cardLayout || 'left-aligned',
                        cardProfilePictureUrl: updatedCard.profilePictureUrl || '',
                        cardCoverPhotoUrl: updatedCard.coverPhotoUrl || '',
                        cardCompanyLogoUrl: updatedCard.companyLogoUrl || '',
                        cardBio: updatedCard.bio || '',
                        customLinkTypeLabels: updatedCard.customLinkTypeLabels || {}
                    }, false);

                    // Also call individual setters to ensure all components update
                    // This ensures TeamMemberAboutContainer's local state syncs properly
                    if (setCardTheme) await setCardTheme(cardId, updatedCard.cardTheme || '');
                    if (setCardLinkColor) await setCardLinkColor(cardId, updatedCard.linkColor || '');
                    if (setContextCardLayout) await setContextCardLayout(cardId, updatedCard.cardLayout || 'left-aligned');
                    if (setCardBio) await setCardBio(cardId, updatedCard.bio || '');

                    // Update image URLs - use null if empty string to clear images
                    if (setCardProfilePictureUrl) {
                        await setCardProfilePictureUrl(cardId, updatedCard.profilePictureUrl || null);
                    }
                    if (setCardCoverPhotoUrl) {
                        await setCardCoverPhotoUrl(cardId, updatedCard.coverPhotoUrl || null);
                    }
                    if (setCardCompanyLogoUrl) {
                        await setCardCompanyLogoUrl(cardId, updatedCard.companyLogoUrl || null);
                    }

                    if (setCustomLinkTypeLabels && updatedCard.customLinkTypeLabels) {
                        await setCustomLinkTypeLabels(cardId, updatedCard.customLinkTypeLabels);
                    }
                }
            } catch (refreshError) {
                console.error('Error refreshing card data:', refreshError);
                // Fallback to page reload if refresh fails
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            }
        } catch (error) {
            console.error('Error applying template:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to apply template',
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleUnassignTemplate = async () => {
        if (!currentTeamId || !cardId) {
            setSnackbar({
                open: true,
                message: 'Missing required information to remove template',
                severity: 'error'
            });
            return;
        }

        try {
            // Get card to find memberId and currently applied template from backend (source of truth)
            const cardResponse = await getCardById(cardId);
            const card = cardResponse?.card;

            if (!card || !card.userId) {
                throw new Error('Could not find card owner');
            }

            const memberId = card.userId;
            const templateId = card.appliedTemplateId;

            if (!templateId) {
                setSnackbar({
                    open: true,
                    message: 'No template is currently applied to this member',
                    severity: 'info'
                });
                return;
            }

            // Use existing assignMembersToTemplate API with unassignMemberIds
            await assignMembersToTemplate(currentTeamId, templateId, {
                assignMemberIds: [],
                unassignMemberIds: [memberId]
            });

            setSnackbar({
                open: true,
                message: 'Template removed successfully',
                severity: 'success'
            });

            // Refresh card data from server so UI reflects backend state
            try {
                await loadCardFromServer(cardId);
            } catch (refreshError) {
                console.error('Error refreshing card data:', refreshError);
            }
        } catch (error) {
            console.error('Error removing template:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to remove template',
                severity: 'error'
            });
            throw error; // Re-throw to let modal know it failed
        }
    };

    return (
        <>
            <TeamMemberEditLayout
                onMenuItemClick={handleMenuItemClick}
                restrictedMode={isNewCard}
                hideOtherSections={isNewCard}
                onAssignToTemplate={handleOpenTemplateModal}
                mainContent={
                    <TeamMemberEditContent
                        selectedItem={selectedItem}
                        user={user}
                        setSelectedItem={setSelectedItem}
                    />
                }
                previewContent={
                    <TeamMemberEditPreview
                        selectedItem={selectedItem}
                        cardId={cardId}
                        user={user}
                    />
                }
            />
            <TeamChooseTemplateModal
                open={templateModalOpen}
                onClose={handleCloseTemplateModal}
                onSelectTemplate={handleSelectTemplate}
                onUnassignTemplate={handleUnassignTemplate}
                cardId={cardId}
            />
            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default TeamMemberEditDashboard;